import { IonButton, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonChip, IonContent, IonIcon, IonItem, IonLabel, IonList, IonListHeader, IonSkeletonText, useIonPopover, useIonAlert } from '@ionic/react';
import styles from './EventClassificationChip.module.css';
import { EventClassification } from '../models/DeviceEvent';
import { arrowUndoOutline, checkmark, checkmarkCircle, chevronExpand, chevronForwardOutline, closeCircleOutline, flagOutline, timeOutline } from 'ionicons/icons';
import { useDeviceEventQuery } from '../query/deviceEventQuery';
import { useEventUserFeedbackQuery, useUpdateEventUserFeedbackMutation, useDeleteEventUserFeedbackMutation } from '../query/eventUserFeedbackQuery';
import { FeedbackResolution, FeedbackType } from '../models/EventUserFeedback';
import { useComponentTranslation } from '../hooks/useTranslation';
import { colorForEventClassification, iconForEventClassification, labelForEventClassification } from '../utils/eventClassificationUtils';
import useUX from '../utils/ux';

interface EventClassificationChipProps {
    deviceId: string;
    eventId: number;
    accessToken?: string | null;
}

interface FeedbackPopoverProps {
    effectiveClassification?: EventClassification;
    feedback?: ReturnType<typeof useEventUserFeedbackQuery>['data'];
    feedbackReviewed: boolean;
    onSubmitFeedback: (feedbackType: FeedbackType) => void;
    onShowOtherIssue: () => void;
    onUndoFeedback: () => void;
}

// Module scope, not inline in the chip: the chip re-renders continuously
// while a video plays (the event modal updates its frame index), and an
// inline component gets a new identity per render, which remounts the open
// popover and resets its scroll position on every tick.
const FeedbackPopover = ({
    effectiveClassification,
    feedback,
    feedbackReviewed,
    onSubmitFeedback,
    onShowOtherIssue,
    onUndoFeedback
}: FeedbackPopoverProps) => {
    const { t } = useComponentTranslation();

    return <IonContent>
        {feedback && <div>
            <IonCardHeader>
                <IonCardTitle>
                    {feedbackReviewed
                        ? t('event_classification_chip.feedback.reviewed_title')
                        : t('event_classification_chip.feedback.submitted_title')}
                </IonCardTitle>
                <IonCardSubtitle>
                    {feedback.feedbackType === FeedbackType.MissedContraband &&
                        t('event_classification_chip.feedback.missed_contraband')}
                    {feedback.feedbackType === FeedbackType.OtherIssue &&
                        t('event_classification_chip.feedback.other_issue')}
                    {feedback.feedbackType === FeedbackType.FalseAlarm &&
                        t('event_classification_chip.feedback.false_alarm')}
                    {feedback.feedbackType === FeedbackType.IncorrectTimeline &&
                        t('event_classification_chip.feedback.incorrect_timeline')}
                </IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
                {!feedbackReviewed && <p>{t('event_classification_chip.feedback.submitted_message')}</p>}
                {feedbackReviewed && <p>{t('event_classification_chip.feedback.reviewed_message')}</p>}
            </IonCardContent>
            {!feedbackReviewed &&
                <IonButton fill="clear" onClick={onUndoFeedback}>
                    {t('event_classification_chip.feedback.cancel_feedback')} <IonIcon icon={arrowUndoOutline} slot="end" />
                </IonButton>
            }
        </div>}

        {!feedback && <>
            <IonCardHeader style={{ paddingBottom: 0 }}>
                <IonCardTitle>{t('event_classification_chip.feedback.title')}</IonCardTitle>
                <IonCardSubtitle>{t('event_classification_chip.feedback.subtitle')}</IonCardSubtitle>
            </IonCardHeader>

            <IonList>
                <IonListHeader>
                    <IonLabel>
                        <h2>{t('event_classification_chip.feedback.report_issue')}</h2>
                    </IonLabel>
                </IonListHeader>
                {effectiveClassification == EventClassification.Contraband
                    && <IonItem button detail={false} onClick={() => onSubmitFeedback(FeedbackType.FalseAlarm)}>
                        <IonIcon slot="start" icon={closeCircleOutline} />
                        <IonLabel>{t('event_classification_chip.feedback.false_alarm')}</IonLabel>
                    </IonItem>}
                {effectiveClassification != EventClassification.Contraband
                    && <IonItem button detail={false} onClick={() => onSubmitFeedback(FeedbackType.MissedContraband)}>
                        <IonIcon slot="start" icon={iconForEventClassification(EventClassification.Contraband)} />
                        <IonLabel>{t('event_classification_chip.feedback.missed_contraband')}</IonLabel>
                    </IonItem>}
                <IonItem button detail={false} onClick={() => onSubmitFeedback(FeedbackType.IncorrectTimeline)}>
                    <IonIcon slot="start" icon={timeOutline} />
                    <IonLabel>{t('event_classification_chip.feedback.incorrect_timeline')}</IonLabel>
                </IonItem>
                <IonItem button detail={false} onClick={onShowOtherIssue}>
                    <IonIcon slot="start" icon={flagOutline} />
                    <IonLabel>{t('event_classification_chip.feedback.other_issue')}</IonLabel>
                </IonItem>
            </IonList>
        </>}
    </IonContent>;
};

const EventClassificationChip: React.FC<EventClassificationChipProps> = ({ deviceId, eventId, accessToken }) => {
    const { t } = useComponentTranslation();
    const eventQuery = useDeviceEventQuery(deviceId, eventId, accessToken);
    const feedbackQuery = useEventUserFeedbackQuery(deviceId, eventId);
    const feedbackMutation = useUpdateEventUserFeedbackMutation(deviceId, eventId);
    const deleteFeedbackMutation = useDeleteEventUserFeedbackMutation(deviceId, eventId);
    const [presentAlert] = useIonAlert();
    const { presentResult } = useUX();
    const event = eventQuery.data;
    const feedback = feedbackQuery.data;
    const classificationChanged = event?.hasManualClassification && (event?.eventManualClassification != event?.eventClassification);
    const feedbackReviewed = !!feedback && feedback.feedbackResolution != null && feedback.feedbackResolution !== FeedbackResolution.Unresolved;

    const [presentPopover, dismissPopover] = useIonPopover(FeedbackPopover, {
        effectiveClassification: event?.eventEffectiveClassification,
        feedback,
        feedbackReviewed,
        onSubmitFeedback: (feedbackType: FeedbackType) => submitFeedback(feedbackType),
        onShowOtherIssue: () => showOtherIssueAlert(),
        onUndoFeedback: () => undoFeedback(),
        onDismiss: (data: any, role: string) => dismissPopover(data, role)
    });

    const submitFeedback = (feedbackType: FeedbackType, feedbackComment?: string) => {
        presentResult(
            () => feedbackMutation.mutateAsync({
                deviceId,
                eventId,
                feedbackType,
                feedbackComment
            }),
            t('event_classification_chip.feedback.submitted_title')
        );
    }

    const showOtherIssueAlert = () => {
        presentAlert({
            header: t('event_classification_chip.feedback.other_issue_alert.title'),
            inputs: [
                {
                    name: 'issueDescription',
                    type: 'textarea',
                    placeholder: t('event_classification_chip.feedback.other_issue_alert.placeholder'),
                    attributes: {
                        maxlength: 500,
                        rows: 4
                    }
                }
            ],
            buttons: [
                {
                    text: t('event_classification_chip.feedback.other_issue_alert.cancel'),
                    role: 'cancel'
                },
                {
                    text: t('event_classification_chip.feedback.other_issue_alert.submit'),
                    handler: (data) => {
                        if (data.issueDescription && data.issueDescription.trim()) {
                            submitFeedback(FeedbackType.OtherIssue, data.issueDescription.trim());
                        } else {
                            submitFeedback(FeedbackType.OtherIssue);
                        }
                    }
                }
            ]
        });
    }

    const undoFeedback = () => {
        deleteFeedbackMutation.mutate();
    }

    if (!event) return <IonChip><IonSkeletonText style={{ width: '50px' }}></IonSkeletonText></IonChip>;

    return (
        <>
            <IonChip color={colorForEventClassification(event.eventEffectiveClassification)} className={classificationChanged ? styles.correctedChip : undefined} onClick={(e: any) => presentPopover({ event: e, dismissOnSelect: true, cssClass: styles.eventClassificationPopover })} >
                {!classificationChanged && <>
                    <IonIcon icon={iconForEventClassification(event.eventEffectiveClassification)} />
                    <IonLabel>{labelForEventClassification(event.eventClassification, t)}</IonLabel>
                    {event.hasManualClassification && <IonIcon icon={checkmark} />}
                </>}
                {/* original over corrected: side by side the pair crushes the
                    event title out of the row on phone widths */}
                {classificationChanged && <div className={styles.correctionStack}>
                    <span className={styles.correctionRow}>
                        <IonIcon icon={iconForEventClassification(event.eventEffectiveClassification)} />
                        <IonLabel>{labelForEventClassification(event.eventClassification, t)}</IonLabel>
                    </span>
                    <span className={styles.correctionRow}>
                        <IonIcon icon={chevronForwardOutline} />
                        <IonLabel>{labelForEventClassification(event.eventManualClassification, t)}</IonLabel>
                    </span>
                </div>}
                {feedback && <>
                    {!feedbackReviewed && <span className={styles.inReview}>{t('event_classification_chip.feedback.status_in_review')}</span>}
                    {feedbackReviewed && <span className={styles.reviewed}>{t('event_classification_chip.feedback.status_reviewed')}</span>}
                </>}
                {(!feedback || feedback.feedbackResolution === FeedbackResolution.Unresolved) && <IonIcon icon={chevronExpand} />}
            </IonChip>
        </>
    );
};

export default EventClassificationChip;
